// src/themes/modern/restaurant/components/RestaurantInfo.tsx
import { motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaAccessibleIcon,
  FaCalendarAlt,
  FaClock,
  FaCreditCard,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFacebookF,
  FaGlobe,
  FaInfoCircle,
  FaInstagram,
  FaMapMarkerAlt,
  FaParking,
  FaPhone,
  FaStar,
  FaTwitter,
  FaUtensils,
  FaWifi,
} from "react-icons/fa";
import {
  FiEye,
  FiSmartphone,
  FiGlobe,
  FiFilter,
  FiAlertTriangle,
} from "react-icons/fi";
import { MdDescription } from "react-icons/md";
import { useBranch } from "~/contexts/BranchContext";
import FeatureCard from "~/pages/home/components/FeatureCard";
import { type Branch } from "~/types/new_types";
import { getBranchAddress, getBranchDescription } from "~/utils/brandHelper";

interface RestaurantInfoProps {
  restaurant: Branch;
}

const RestaurantInfo: React.FC<RestaurantInfoProps> = ({ restaurant }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const { features } = useBranch();

  // Helper function to convert icon strings to React components
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      FiEye: <FiEye size={20} />,
      FiSmartphone: <FiSmartphone size={20} />,
      FiGlobe: <FiGlobe size={20} />,
      FiFilter: <FiFilter size={20} />,
      FiAlertTriangle: <FiAlertTriangle size={20} />,
      FaWifi: <FaWifi size={20} />,
      FaParking: <FaParking size={20} />,
      FaUtensils: <FaUtensils size={20} />,
    };

    return iconMap[iconName] || <FaInfoCircle size={20} />; // fallback icon
  };
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-12 gap-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Main Info Column */}
      <motion.div className="md:col-span-8 space-y-6" variants={itemVariants}>
        {/* description Section */}
        <motion.div
          className="rounded-2xl overflow-hidden"
          style={{
            backgroundColor: restaurant.theme.cardColor,
            borderRadius: restaurant.theme.borderRadius,
            boxShadow: `0 10px 25px -5px ${restaurant.theme.primaryColor}10`,
          }}
          whileHover={{
            y: -5,
            boxShadow: `0 20px 30px -10px ${restaurant.theme.primaryColor}20`,
          }}
          variants={itemVariants}
        >
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: `${restaurant.theme.primaryColor}15`,
                  color: restaurant.theme.primaryColor,
                }}
              >
                <MdDescription size={20} />
              </div>
              <h2
                className="text-2xl font-bold"
                style={{ color: restaurant.theme.primaryColor }}
              >
                {t("restaurant.info.description")}
              </h2>
            </div>

            <p>{getBranchDescription(restaurant, i18n.language)}</p>
          </div>
        </motion.div>
        {/* Special Features Section */}
        {features && features.length > 0 && (
          <motion.div
            className="rounded-3xl overflow-hidden"
            style={{
              backgroundColor: restaurant.theme.cardColor,
              borderRadius: restaurant.theme.borderRadius,
              boxShadow: `0 15px 35px -5px ${restaurant.theme.primaryColor}15`,
            }}
            whileHover={{
              y: -8,
              boxShadow: `0 25px 40px -10px ${restaurant.theme.primaryColor}25`,
            }}
            variants={itemVariants}
            transition={{ duration: 0.4 }}
          >
            <div className="p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${restaurant.theme.primaryColor}15`,
                    color: restaurant.theme.primaryColor,
                  }}
                >
                  <FaCalendarAlt size={24} />
                </div>
                <div>
                  <h2
                    className="text-3xl font-bold mb-1"
                    style={{ color: restaurant.theme.primaryColor }}
                  >
                    {t("restaurant.info.specialFeatures")}
                  </h2>
                  <p className="text-gray-600">
                    {t("restaurant.info.specialFeaturesDescription")}
                  </p>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <FeatureCard key={index} feature={feature} />
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Location */}
        {restaurant?.address && (
          <motion.div
            className="rounded-2xl overflow-hidden group"
            style={{
              backgroundColor: restaurant.theme.cardColor,
              borderRadius: restaurant.theme.borderRadius,
              boxShadow: `0 10px 25px -5px ${restaurant.theme.primaryColor}10`,
            }}
            whileHover={{
              y: -5,
              boxShadow: `0 20px 30px -10px ${restaurant.theme.primaryColor}20`,
            }}
            variants={itemVariants}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors group-hover:scale-105"
                  style={{
                    backgroundColor: `${restaurant.theme.primaryColor}15`,
                    color: restaurant.theme.primaryColor,
                  }}
                >
                  <FaMapMarkerAlt />
                </div>
                <h3
                  className="font-bold text-xl"
                  style={{ color: restaurant.theme.textColor }}
                >
                  {t("restaurant.info.address")}
                </h3>
              </div>

              <div className="space-y-3">
                <address
                  className="not-italic p-3 rounded-lg whitespace-pre-wrap"
                  style={{
                    backgroundColor: `${restaurant.theme.primaryColor}08`,
                    color: restaurant.theme.textColor,
                    borderRadius: restaurant.theme.borderRadius,
                  }}
                >
                  <bdi>{getBranchAddress(restaurant, i18n.language)}</bdi>
                </address>

                {(restaurant.location?.google_maps_link ||
                  restaurant.location?.coordinates) && (
                  <a
                    href={
                      restaurant.location?.google_maps_link ??
                      `https://www.google.com/maps?q=${restaurant.location?.coordinates}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 py-2 px-3 rounded-lg hover:underline"
                    style={{
                      backgroundColor: `${restaurant.theme.primaryColor}15`,
                      color: restaurant.theme.primaryColor,
                      borderRadius: restaurant.theme.borderRadius,
                    }}
                  >
                    <span>{t("restaurant.info.viewOnGoogleMaps")}</span>
                    <FaExternalLinkAlt size={12} />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <motion.div className="md:col-span-4 space-y-6">
        {/* Hours */}
        <motion.div
          className="rounded-2xl overflow-hidden"
          style={{
            backgroundColor: restaurant.theme.cardColor,
            borderRadius: restaurant.theme.borderRadius,
            boxShadow: `0 10px 25px -5px ${restaurant.theme.primaryColor}10`,
          }}
          whileHover={{
            y: -5,
            boxShadow: `0 20px 30px -10px ${restaurant.theme.primaryColor}20`,
          }}
          variants={itemVariants}
        >
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: `${restaurant.theme.primaryColor}15`,
                  color: restaurant.theme.primaryColor,
                }}
              >
                <FaClock />
              </div>
              <h3
                className="font-bold text-xl"
                style={{ color: restaurant.theme.textColor }}
              >
                {t("restaurant.info.hours")}
              </h3>
            </div>

            <div className="space-y-1">
              {restaurant.operating_hours?.map((item, index) => {
                const isToday =
                  item.day.toLowerCase() ===
                  new Date()
                    .toLocaleDateString("en-US", { weekday: "long" })
                    .toLowerCase();
                return (
                  <motion.div
                    key={index}
                    className={`flex justify-between py-2 px-3 rounded-lg transition-colors ${
                      isToday ? "" : ""
                    }`}
                    style={{
                      backgroundColor: isToday
                        ? `${restaurant.theme.primaryColor}15`
                        : `${restaurant.theme.primaryColor}0`,
                      borderRadius: restaurant.theme.borderRadius,
                      borderLeft:
                        isToday && !isRTL
                          ? `3px solid ${restaurant.theme.primaryColor}`
                          : undefined,
                      borderRight:
                        isToday && isRTL
                          ? `3px solid ${restaurant.theme.primaryColor}`
                          : undefined,
                    }}
                    whileHover={{
                      backgroundColor: `${restaurant.theme.primaryColor}10`,
                      x: isRTL ? -4 : 4,
                    }}
                  >
                    <span
                      className={`font-medium capitalize ${
                        isToday ? "font-bold" : ""
                      }`}
                      style={{
                        color: isToday
                          ? restaurant.theme.primaryColor
                          : restaurant.theme.textColor,
                      }}
                    >
                      {t(`weekDays.${item.day.toLowerCase()}`)}
                    </span>
                    <span
                      style={{
                        color: item.is_closed
                          ? "red"
                          : restaurant.theme.textColor,
                      }}
                    >
                      {item.is_closed ? (
                        <span
                          className="px-3 py-1 rounded text-xs"
                          style={{
                            backgroundColor: "rgba(239, 68, 68, 0.1)",
                            color: "rgb(239, 68, 68)",
                            borderRadius: restaurant.theme.borderRadius,
                          }}
                        >
                          {t("restaurant.info.closed")}
                        </span>
                      ) : (
                        <span dir="ltr">
                          <span style={{ color: restaurant.theme.accentColor }}>
                            {item.opening_time}
                          </span>
                          <span className="mx-1">-</span>
                          <span
                            style={{ color: restaurant.theme.secondaryColor }}
                          >
                            {item.closing_time}
                          </span>
                        </span>
                      )}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          className="rounded-2xl overflow-hidden group"
          style={{
            backgroundColor: restaurant.theme.cardColor,
            borderRadius: restaurant.theme.borderRadius,
            boxShadow: `0 10px 25px -5px ${restaurant.theme.primaryColor}10`,
          }}
          whileHover={{
            y: -5,
            boxShadow: `0 20px 30px -10px ${restaurant.theme.primaryColor}20`,
          }}
          variants={itemVariants}
        >
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center transition-colors group-hover:scale-105"
                style={{
                  backgroundColor: `${restaurant.theme.primaryColor}15`,
                  color: restaurant.theme.primaryColor,
                }}
              >
                <FaPhone />
              </div>
              <h3
                className="font-bold text-xl"
                style={{ color: restaurant.theme.textColor }}
              >
                {t("restaurant.info.contactInfo")}
              </h3>
            </div>

            <div className="space-y-4">
              <div
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{
                  backgroundColor: `${restaurant.theme.primaryColor}08`,
                  borderRadius: restaurant.theme.borderRadius,
                }}
              >
                <FaPhone style={{ color: restaurant.theme.primaryColor }} />
                <a
                  href={`tel:${restaurant.phone}`}
                  className="hover:underline"
                  style={{ color: restaurant.theme.textColor }}
                >
                  <bdi>{restaurant.phone ?? t("common.notAvailable")}</bdi>
                </a>
              </div>

              <div
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{
                  backgroundColor: `${restaurant.theme.primaryColor}08`,
                  borderRadius: restaurant.theme.borderRadius,
                }}
              >
                <FaEnvelope style={{ color: restaurant.theme.primaryColor }} />
                <a
                  href={`mailto:${restaurant.email}`}
                  className="hover:underline truncate"
                  style={{ color: restaurant.theme.textColor }}
                >
                  {restaurant.email ?? t("common.notAvailable")}
                </a>
              </div>

              <div className="flex gap-4 justify-center mt-6">
                {restaurant.social_media?.facebook && (
                  <motion.a
                    href={restaurant.social_media?.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: `${restaurant.theme.primaryColor}15`,
                      color: restaurant.theme.primaryColor,
                    }}
                    whileHover={{
                      y: -4,
                      backgroundColor: restaurant.theme.primaryColor,
                      color: "#fff",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaFacebookF />
                  </motion.a>
                )}

                {restaurant.social_media?.instagram && (
                  <motion.a
                    href={restaurant.social_media?.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: `${restaurant.theme.primaryColor}15`,
                      color: restaurant.theme.primaryColor,
                    }}
                    whileHover={{
                      y: -4,
                      backgroundColor: restaurant.theme.primaryColor,
                      color: "#fff",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaInstagram />
                  </motion.a>
                )}

                {restaurant.social_media?.twitter && (
                  <motion.a
                    href={restaurant.social_media?.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: `${restaurant.theme.primaryColor}15`,
                      color: restaurant.theme.primaryColor,
                    }}
                    whileHover={{
                      y: -4,
                      backgroundColor: restaurant.theme.primaryColor,
                      color: "#fff",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaTwitter />
                  </motion.a>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default RestaurantInfo;
